import React, {useCallback, useMemo, useState} from 'react'

interface DeleteTaskDialogState {
  isOpen: boolean
  taskId?: string
  repoOwner?: string
  repoName?: string
}

interface DeleteTaskDialogContextValue {
  dialogState: DeleteTaskDialogState
  chatApiUrl?: string
  openDeleteDialog: (taskId: string, repoOwner?: string, repoName?: string) => void
  closeDeleteDialog: () => void
}

const DeleteTaskDialogContext = React.createContext<DeleteTaskDialogContextValue | undefined>(undefined)

interface DeleteTaskDialogProviderProps {
  children: React.ReactNode
  chatApiUrl?: string
}

const initialState: DeleteTaskDialogState = {isOpen: false}

export function DeleteTaskDialogProvider({children, chatApiUrl}: DeleteTaskDialogProviderProps) {
  const [dialogState, setDialogState] = useState<DeleteTaskDialogState>(initialState)

  const openDeleteDialog = useCallback((taskId: string, repoOwner?: string, repoName?: string) => {
    setDialogState({isOpen: true, taskId, repoOwner, repoName})
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDialogState(initialState)
  }, [])

  const value = useMemo(
    () => ({dialogState, chatApiUrl, openDeleteDialog, closeDeleteDialog}),
    [chatApiUrl, dialogState, openDeleteDialog, closeDeleteDialog],
  )

  return <DeleteTaskDialogContext value={value}>{children}</DeleteTaskDialogContext>
}

export function useDeleteTaskDialog() {
  const context = React.use(DeleteTaskDialogContext)
  if (!context) {
    throw new Error('useDeleteTaskDialog must be used within a DeleteTaskDialogProvider')
  }
  return context
}

/**
 * Optional version of useDeleteTaskDialog that returns undefined if not within a provider.
 * Useful for components that may be rendered in contexts where deletion isn't available.
 */
export function useDeleteTaskDialogOptional() {
  return React.use(DeleteTaskDialogContext)
}
